import React from "react";
import { cn } from "tailwind-variants";
import { useI18n } from "intl/useI18n";
import { useDtfStore } from "../store";
import "./MultiSizeTotalSummary.scss";
import { renderList } from "common/vue/render-list";

export default function MultiSizeTotalSummary({ index, ...restProps }) {
  const store = useDtfStore();
  const { t, n } = useI18n();

  function artworks() {
    return store.artworks;
  }

  function summary() {
    return store.summary;
  }

  function simulation() {
    return store.simulation;
  }

  function isPricingLoading() {
    return store.isPricingLoading;
  }

  function artwork() {
    return artworks()[index];
  }

  function isArtworkUploaded() {
    const artworkId = parseInt(artwork()?.id.toString() ?? "", 10);
    if (Number.isNaN(artworkId)) {
      return false;
    }
    return typeof artworkId === "number" && !artwork()?.isLoading;
  }

  function showPricing() {
    return index === 0 || isArtworkUploaded();
  }

  function sizesAndQuantities() {
    return artwork()?.sizesAndQuantities ?? [];
  }

  function sizesCount() {
    return sizesAndQuantities().length;
  }

  function quantity() {
    return sizesAndQuantities().reduce((acc, curr) => acc + Number(curr.quantity), 0);
  }

  function pricing() {
    if (artwork()?.isFile || !sizesAndQuantities().some(sq => Number(sq.quantity) > 0)) {
      return simulation();
    }
    return sizesAndQuantities() || simulation();
  }

  function enableAbAiMonetization() {
    return store.enableAbAiMonetization;
  }

  function getBogoAdjustedPrice() {
    return store.getBogoAdjustedPrice;
  }

  function isBogoApplicable() {
    return store.isBogoApplicable;
  }

  function getPrice(propertyName) {
    if (Array.isArray(pricing())) {
      const artworkId = String(artwork()?.id ?? "");
      return pricing()
        .reduce((sum, item) => {
          // Check for BOGO-adjusted price for unit price
          if (propertyName === "unitPrice" && artworkId) {
            const sizeKey = `${item.width}x${item.height}`;
            const bogoPrice = getBogoAdjustedPrice()(artworkId, item.id, sizeKey);
            if (bogoPrice) {
              return sum + bogoPrice.discountedTotalPrice;
            }
          }
          const price = Number(item[propertyName] || 0);
          return sum + price * item.quantity;
        }, 0)
        .toFixed(2);
    }
    const price = pricing()[propertyName];
    if (!price) {
      return null;
    }
    return Number(Number(price).toFixed(2)) * pricing().quantity;
  }

  function originalTotal() {
    if (!Array.isArray(pricing())) {
      const price = pricing()?.unitPrice;
      const qty = pricing()?.quantity ?? 0;
      if (!price || qty === 0) return null;
      return Number(Number(price).toFixed(2)) * qty;
    }
    return pricing()
      .reduce((sum, item) => {
        const price = Number(item.unitPrice || 0);
        return sum + price * item.quantity;
      }, 0)
      .toFixed(2);
  }

  function bogoInfo() {
    if (!isBogoApplicable() || !Array.isArray(pricing())) {
      return {
        freeQty: 0,
        discount: 0
      };
    }
    const artworkId = String(artwork()?.id ?? "");
    let freeQty = 0;
    let discountAmount = 0;
    pricing().forEach(item => {
      const sizeKey = `${item.width}x${item.height}`;
      const bogoPrice = getBogoAdjustedPrice()(artworkId, item.id, sizeKey);
      if (bogoPrice && bogoPrice.freeQty > 0) {
        freeQty += bogoPrice.freeQty;
        const itemOriginalTotal = Number(item.unitPrice || 0) * item.quantity;
        discountAmount += itemOriginalTotal - bogoPrice.discountedTotalPrice;
      }
    });
    return {
      freeQty,
      discount: discountAmount
    };
  }

  function retailTotal() {
    return getPrice("retailPrice");
  }

  function total() {
    return getPrice("unitPrice");
  }

  function discount() {
    const retail = parseFloat(retailTotal()?.toString() ?? "");
    const actual = parseFloat(total()?.toString() ?? "");
    if (!retail || !actual) return 0;
    return Number(Math.ceil(((retail - actual) / retail) * 100).toFixed(0));
  }

  function enhancements() {
    if (!summary().enhancements) {
      return [];
    }
    return Object.values(summary().enhancements);
  }

  function orderSubtotal() {
    return Number(summary().orderSubtotal);
  }

  function artworksReady() {
    return artwork()?.isSimulated || (!artwork()?.isLoading && artwork()?.enhancementCompleted);
  }

  return (
    <>
      {pricing() ? (
        <div
          {...restProps}
          className={cn("multi-size-total-summary scope-3cac75a9", restProps?.className, {
            "multi-size-total-summary--loading":
              !enableAbAiMonetization() && (isPricingLoading() || total() === 0)
          })}
        >
          <h3 className="multi-size-total-summary__caption scope-3cac75a9">Total</h3>
          {enableAbAiMonetization() && (isPricingLoading() || !artworksReady() || total() === 0) ? (
            <div className="multi-size-total-summary__skeleton scope-3cac75a9">
              <div className="multi-size-total-summary__skeleton-block scope-3cac75a9" />
              <div className="multi-size-total-summary__skeleton-block scope-3cac75a9" />
              <div className="multi-size-total-summary__skeleton-block scope-3cac75a9" />
            </div>
          ) : (
            <div className="multi-size-total-summary__grid scope-3cac75a9">
              {sizesCount() > 0 ? (
                <>
                  <div className="multi-size-total-summary__item scope-3cac75a9">
                    <span className="multi-size-total-summary__label scope-3cac75a9">
                      <span className="multi-size-total-summary__info scope-3cac75a9">for</span>{" "}
                      {t("transfer", quantity())}
                    </span>
                  </div>
                  {showPricing() ? (
                    <div className="multi-size-total-summary__item scope-3cac75a9">
                      <span className="multi-size-total-summary__price scope-3cac75a9">
                        {n(bogoInfo().freeQty > 0 ? originalTotal() : total(), "currency")}
                      </span>
                      {retailTotal() && discount() > 0 && bogoInfo().freeQty === 0 ? (
                        <span className="multi-size-total-summary__old-price scope-3cac75a9">
                          {n(retailTotal(), "currency")}
                        </span>
                      ) : null}
                      {discount() > 0 && bogoInfo().freeQty === 0 ? (
                        <span className="multi-size-total-summary__discount scope-3cac75a9">
                          {discount()}% off{" "}
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                  {bogoInfo().freeQty > 0 ? (
                    <>
                      <div className="multi-size-total-summary__item scope-3cac75a9">
                        <span className="multi-size-total-summary__label multi-size-total-summary__label--bogo scope-3cac75a9">
                          {bogoInfo().freeQty} FREE{" "}
                          {bogoInfo().freeQty === 1 ? "Transfer" : "Transfers"}
                        </span>
                      </div>
                      <div className="multi-size-total-summary__item scope-3cac75a9 -ml-[6.5px]">
                        <span className="multi-size-total-summary__price multi-size-total-summary__price--bogo scope-3cac75a9">
                          {" "}
                          -{n(bogoInfo().discount, "currency")}
                        </span>
                      </div>
                    </>
                  ) : null}
                </>
              ) : null}
              {renderList(enhancements(), enhancement => (
                <>
                  {Number(enhancement.totalPrice) > 0 ? (
                    <>
                      <div className="multi-size-total-summary__item scope-3cac75a9">
                        <span className="multi-size-total-summary__label scope-3cac75a9">
                          <span className="multi-size-total-summary__info scope-3cac75a9">for</span>{" "}
                          {enhancement.quantity} {enhancement.serviceName}
                        </span>
                      </div>
                      <div className="multi-size-total-summary__item scope-3cac75a9">
                        <span className="multi-size-total-summary__price scope-3cac75a9">
                          {n(enhancement.totalPrice, "currency")}
                        </span>
                      </div>
                    </>
                  ) : null}
                </>
              ))}
              {orderSubtotal() || bogoInfo().freeQty > 0 ? (
                <>
                  <div className="multi-size-total-summary__item scope-3cac75a9">
                    <span className="multi-size-total-summary__label scope-3cac75a9">Subtotal</span>
                  </div>
                  <div className="multi-size-total-summary__item scope-3cac75a9">
                    <span className="multi-size-total-summary__price scope-3cac75a9">
                      {n(bogoInfo().freeQty > 0 ? total() : orderSubtotal(), "currency")}
                    </span>
                    {bogoInfo().discount > 0 ? (
                      <span className="multi-size-total-summary__savings scope-3cac75a9">
                        {" "}
                        You save {n(bogoInfo().discount, "currency")}!{" "}
                      </span>
                    ) : null}
                  </div>
                </>
              ) : null}
            </div>
          )}
        </div>
      ) : null}
    </>
  );
}
